import React, { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { Chart } from 'chart.js';

const STORAGE_KEY = 'climateplans-theme';
export type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'dark' || stored === 'light') return stored;
  return 'light';
}

function applyThemeToDocument(theme: Theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
}

function applyChartDefaults(theme: Theme) {
  const isDark = theme === 'dark';
  const textColor = isDark ? '#94a3b8' : '#475569';
  const gridColor = isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(71, 85, 105, 0.2)';

  Chart.defaults.color = textColor;
  Chart.defaults.borderColor = gridColor;

  if (!Chart.defaults.scales) Chart.defaults.scales = {} as any;
  const scaleDefaults = (Chart.defaults as any).scales || {};
  if (!scaleDefaults.x) scaleDefaults.x = {};
  if (!scaleDefaults.y) scaleDefaults.y = {};
  scaleDefaults.x.grid = { ...scaleDefaults.x.grid, color: gridColor };
  scaleDefaults.x.ticks = { ...scaleDefaults.x.ticks, color: textColor };
  scaleDefaults.x.title = { ...scaleDefaults.x.title, color: textColor };
  scaleDefaults.y.grid = { ...scaleDefaults.y.grid, color: gridColor };
  scaleDefaults.y.ticks = { ...scaleDefaults.y.ticks, color: textColor };
  scaleDefaults.y.title = { ...scaleDefaults.y.title, color: textColor };
  (Chart.defaults as any).scales = scaleDefaults;

  if (!Chart.defaults.plugins) (Chart.defaults as any).plugins = {};
  const plugins = (Chart.defaults as any).plugins;
  if (!plugins.title) plugins.title = {};
  if (!plugins.legend) plugins.legend = {};
  plugins.title.color = textColor;
  plugins.legend.labels = { ...plugins.legend.labels, color: textColor };
}

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => getStoredTheme());

  useEffect(() => {
    applyThemeToDocument(theme);
    window.localStorage.setItem(STORAGE_KEY, theme);
    applyChartDefaults(theme);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  const value: ThemeContextType = { theme, setTheme, toggleTheme };
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const ctx = useContext(ThemeContext);
  if (ctx === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
};
